/*
The router's authentication guard (KAN-39, XOL-602/603/604).

This was inline in src/main.js; it lives here so a spec can run the REAL guard (the old
routeGuard.spec re-implemented it, so it could not catch a change to the real one).
main.js registers it: `router.beforeEach(createAuthGuard({ isAuthenticated, setPostLoginRedirect, store }))`,
the same shape as createLeaveGuard.

Three kinds of path (lib/publicRoutes.js):

  /logout                       always reachable, whatever the auth state.

  /login and the signed-out-only pages (/forgot-password, /reset-password/<token>,
  /set-password/<token>, /register/<token>, /password-set):
    signed out -> through, WITHOUT storing a post-login redirect. Storing one would write a
                  single-use token into sessionStorage. The stale store.state.isLoggedIn is
                  cleared first, or App.vue would render the page inside <home>.
    signed in  -> to the app. Decision D11: a signed-in browser ignores a password link or an
                  invitation, so one can never be used from a live session. The link page never
                  renders. The old /login rule already did this.

  everything else               needs a valid access token; otherwise the intended route is
                                stored (setPostLoginRedirect) and the user goes to /login.

"Signed in" is authLib.isAuthenticated(): an access JWT that has not expired, the same test
the /login rule has always used. A browser holding only stale tokens therefore gets the link
page, which is safe: every call those pages make is public. The persisted isLoggedIn flag
is NOT safe to leave alone (see above), so the guard resets it on those paths.

The redirect for a signed-out-only page REPLACES the history entry. With a plain `next('/')`
vue-router pushes, and Back from the app would land on the token URL again.

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import { isLoginPath, isLogoutPath, isSignedOutOnlyPath } from '@/lib/publicRoutes'

/**
 * @param {Object} deps
 * @param {Function} deps.isAuthenticated () => boolean
 * @param {Function} deps.setPostLoginRedirect (fullPath) => void
 * @param {Object} deps.store Vuex-like store; `store.state.isLoggedIn` is kept in step
 * @returns {Function} a router.beforeEach guard
 */
export function createAuthGuard (deps) {
  var isAuthenticated = deps.isAuthenticated
  var setPostLoginRedirect = deps.setPostLoginRedirect
  var store = deps.store

  return function authGuard (to, from, next) {
    // KAN-39: allowlist auth-related routes; everything else needs a valid JWT.
    var path = to.path || ''

    if (isLogoutPath(path)) {
      // /logout remains reachable regardless of auth state.
      next()
      return
    }

    if (isLoginPath(path)) {
      // Authenticated users navigating to /login are sent home silently.
      if (isAuthenticated()) {
        next('/')
        return
      }
      // A stale `isLoggedIn: true` (browser closed without logging out; the access token has
      // since expired) must not survive: App.vue keys on it and would render the app chrome.
      store.state.isLoggedIn = false
      next()
      return
    }

    if (isSignedOutOnlyPath(path)) {
      // D11: signed in -> the link is ignored. Signed out -> the page, with nothing stored.
      if (isAuthenticated()) {
        next({ path: '/', replace: true })
        return
      }
      // Same stale-flag reason as /login above: without this the link page would render
      // inside <home>, under the previous user's nav bar.
      store.state.isLoggedIn = false
      next()
      return
    }

    if (isAuthenticated()) {
      if (!store.state.isLoggedIn) {
        store.state.isLoggedIn = true
      }
      next()
    } else {
      store.state.isLoggedIn = false
      setPostLoginRedirect(to.fullPath)
      next('/login')
    }
  }
}
